import {useState} from 'react';

import {SimpleGrid} from '@mantine/core';

import {useMeteringRecords} from '../hooks/useMeteringRecords.ts';
import {useMeteringRecordsTableColumns} from '../hooks/useMeteringRecordsTableColumns.tsx';

import {
    DataTableTokenPagination,
    MantineDataTable,
    TableSearchForm,
    useTableSort,
} from '@flexinet/ui-components';

import {MeteringRecord} from '../types';
import {useAuth} from "../../../hooks/useAuth.ts";
import {useVerifiedPermissions} from '@flexinet/auth-components';
import {FormDrawer} from '../drawers/FormDrawer';
import {update} from '../services/MeteringRecordsService';
import {stripReadonlyFields} from '../../../utils/stripReadonlyFields';
import {useQueryClient} from '@tanstack/react-query';
import {I18n} from '@aws-amplify/core';

export const RecordsTable = ({
                                 selectedRecords,
                                 setSelectedRecords,
                             }: {
    selectedRecords: MeteringRecord[],
    setSelectedRecords: (records: MeteringRecord[]) => void,
}) => {

    const {allowedActions} = useVerifiedPermissions();
    const queryClient = useQueryClient();
    const [drawerOpened, setDrawerOpened] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState<MeteringRecord | null>(null);

    const {
        user,
    } = useAuth();

    if (!user) {
        throw new Error('user is required parameter');
    }

    const cognitoUsername = user.username;

    const {
        visibleItems,
        loading,
        nextToken,
        isFetchingMore,
        fetchRecords,
        handleFilterByKeyword,
    } = useMeteringRecords();

    // Newest first. `created` is the only creation timestamp MeteringRecord has; it is
    // nullable in the schema, so rows without it sort last in both directions.
    // Under token pagination this sorts only the pages fetched so far, not the
    // whole dataset: a property of the model, not a bug.
    const {sortStatus, setSortStatus, sorted} = useTableSort<MeteringRecord>(visibleItems, {
        columnAccessor: 'created',
        direction: 'desc',
    });

    const handleRecordClick = (record: MeteringRecord) => {
        setSelectedRecord(record);
        setDrawerOpened(true);
    };

    const columns = useMeteringRecordsTableColumns(handleRecordClick);

    const handleDrawerSubmit = async (values: MeteringRecord) => {
        if (selectedRecord?.id) {
            await update(selectedRecord.id, stripReadonlyFields(values) as MeteringRecord);
            queryClient.invalidateQueries({queryKey: ['metering-records']});
        }
    };

    // Only the creator or manager of a metering record may select it for deletion.
    const selectionProps = allowedActions.includes('DeleteMeteringRecord') ? {
        selectedRecords,
        onSelectedRecordsChange: setSelectedRecords,
        isRecordSelectable: (item: MeteringRecord) =>
            item.createdByUsername === cognitoUsername || item.managedByUsername === cognitoUsername,
    } : {};

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            <MantineDataTable<MeteringRecord>
                verticalSpacing='sm'
                striped
                highlightOnHover
                minHeight={150}
                records={sorted}
                columns={columns}
                fetching={loading}
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                noRecordsText={I18n.get('No records found')}
                {...selectionProps}
            />

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                nextToken={nextToken}
                handleLoadMore={fetchRecords}
                isFetchingMore={isFetchingMore}
                noun='metering record'
            />

            <FormDrawer
                record={selectedRecord || {} as MeteringRecord}
                handleSubmit={handleDrawerSubmit}
                opened={drawerOpened}
                onClose={() => setDrawerOpened(false)}
            />
        </SimpleGrid>
    );
};
